// ⚠️ BANCO DE PRUEBAS — VARIANTE v2 DEL MODO MODELO. TEMPORAL.
//
// La columna izquierda de `/interfaz-v2/modo/modelo-v2` en su rediseño: UNA sola
// pantalla con todo lo que el modo necesita, de arriba abajo —subir/elegir producto,
// Modelo, Pose, los ajustes, la instrucción y Generar—, en vez de los dos pasos que
// tenía (elegir producto → Siguiente → configurar).
//
// Es el pedido del 2026-08-21 sobre una referencia visual; ver
// ADR-2026-08-21-modelo-v2-una-sola-columna.
//
// NO reemplaza a `ModeloPanel`: ése sigue siendo el panel del paso 2 de las
// otras variantes. Éste es la columna entera de v2 y monta los MISMOS pickers, que se
// abren como sheets/paneles laterales desde `InterfazV2` — acá sólo se dibuja el
// disparador y el valor elegido.
//
// Se borra al cerrar el rediseño; si v2 gana, esto se promueve.

import React from 'react';
import { ChevronRight, Palette, Pencil, Plus, Sparkles, Type as TypeIcon } from 'lucide-react';
import { detalleDeSeleccion, type SeleccionDeMoodboard } from './PanelMoodboardModelo';
import CachedImg from '../../../components/CachedImg';
import { modelSelectionLabel, type ModelSelection } from '../ModelPicker';
import {
  fondoBlancoAjustesLabel,
  fondoSelectionLabel,
  type FondoSelection,
} from '../FondoPicker';
import { type CalidadImagen } from '../components/AjustesRapidosProducto';
import { PromptRecientesButton } from '../components/PromptRecientesButton';
import { PieConfiguracionModo } from '../components/PieConfiguracionModo';
import type { MockProductV2 } from '../mockData';
import { useUserProducts } from '../productsContext';
import type { ModelV2 } from '../modelsApi';
import type { PoseV2 } from '../posesApi';
import { palabraDeModeloActual, usePalabraDeModelo } from '../../../lib/nombreDeModelo';

export interface PanelCompactoProps {
  /** Productos elegidos. Vacío = todavía no eligió ninguno. */
  products: MockProductV2[];
  /** Abre el panel lateral con todos los productos cargados. */
  onAbrirProductos: () => void;
  /** Archivos soltados sobre la zona de subida. */
  onSubirArchivos: (files: FileList | File[]) => void;

  modelSelection: ModelSelection | null;
  onOpenModelPicker: () => void;
  /** Cuántos modelos hay elegidos — ver `modelosElegidos` en `CardsModeloPose`. */
  modelosElegidos?: number;
  /** El grupo entero y sus productos — ver `QuienLlevaQueV2`. */
  modelosGrupo?: ModelSelection[];
  productosPorModelo?: Record<number, MockProductV2[]>;
  onTapProductosModelo?: (idx: number) => void;
  /** Modelos del usuario. Se usan para re-resolver la miniatura — ver `modeloImagen`. */
  userModels: ModelV2[];

  allPoses: PoseV2[];
  customPoses: PoseV2[];
  poseIds: string[];
  onOpenPosePicker: () => void;

  calidad: CalidadImagen;
  onCalidadChange: (v: CalidadImagen) => void;
  /** Cuántas imágenes genera la tanda — el mismo control que el sheet de mobile. */
  cantidad: number;
  cantidadMax: number;
  onCantidadChange: (v: number) => void;
  /**
   * ¿Se dibuja el contador? (`TandaModelo.contadorAplica`)
   *
   * Con 2+ poses NO: la tanda la fija el catálogo de poses —una imagen por cada una— y
   * un contador que no multiplica nada es un control muerto que además contradice al
   * botón (ADR-2026-09-04-con-varias-poses-el-contador-se-esconde). Default `true`
   * para los call-sites que no deciden nada de esto.
   */
  contadorVisible?: boolean;
  /** La card de Fondo necesita la selección entera para mostrar su foto. */
  fondoSelection: FondoSelection | null;
  onOpenFondo: () => void;
  /** La card de Moodboard — ver `moodboard` en `CardsModeloPose`. Sin `onOpenMoodboard`
   *  no se dibuja (las marcas que no ven moodboards). */
  moodboard?: SeleccionDeMoodboard | null;
  onOpenMoodboard?: () => void;
  formatoLabel: string;
  onOpenFormato: () => void;

  otraConfig: string;
  onOtraChange: (v: string) => void;

  /** Cuántas imágenes salen de la tanda — va en el texto del CTA. */
  cantidadImagenes: number;
  creditCost: number | null;
  onGenerate: () => void;
  /** Con algo sin elegir el CTA se apaga y esto dice qué falta. */
  falta?: string;
}

/**
 * Las ilustraciones de producto: el respaldo de la card vacía cuando el usuario TODAVÍA
 * no tiene catálogo (con catálogo muestra sus tres últimos —
 * ADR-2026-09-04-la-card-vacia-muestra-productos-de-verdad), y la primera como respaldo
 * de una miniatura sin foto propia.
 *
 * Son assets y no íconos de la librería: la card muestra fotos de producto, y un trazo
 * de `lucide` al lado de una foto se lee como un ícono de sistema, no como "acá iría un
 * producto".
 */
/**
 * Cuánto se inclina cada ilustración, en grados (una a cada lado).
 *
 * A 30° —el primer intento— las prendas quedaban francamente torcidas y costaba
 * reconocerlas en 50px. Con menos, la inclinación se sigue leyendo como gesto y la foto
 * se sigue leyendo como prenda.
 */
const ANGULO_PLACEHOLDER = 10;

const PLACEHOLDERS_PRODUCTO = [
  '/images/modo_con_modelo/placeholder.png',
  '/images/modo_con_modelo/jean_placeholder.png',
  '/images/modo_con_modelo/zapatillas_placeholder.png',
];

/**
 * Miniatura de un producto en la card.
 *
 * Sin foto —un producto dado de alta sin imagen, o una que no cargó— va la ilustración
 * en vez del recuadro blanco vacío, que no se distinguía de un hueco del layout.
 */
const MiniaturaProducto: React.FC<{
  producto: MockProductV2;
  className?: string;
  atenuada?: boolean;
  style?: React.CSSProperties;
}> = ({ producto, className = '', atenuada, style }) => (
  <span
    style={style}
    className={`rounded-xl overflow-hidden bg-white block ${className}`}
  >
    {producto.imageUrl ? (
      <CachedImg
        src={producto.imageUrl}
        alt={atenuada ? '' : producto.name}
        className="w-full h-full object-cover"
      />
    ) : (
      <img
        src={PLACEHOLDERS_PRODUCTO[0]}
        alt=""
        className="w-full h-full object-cover"
      />
    )}
  </span>
);

/**
 * Cuántas miniaturas entran en la card de Productos.
 *
 * Cuatro de 52px con sus separaciones es lo que queda a la izquierda del nombre y el
 * chevron en una columna de ~380px. Con más elegidos, la última pasa a ser el "+N".
 */
const PREVIEWS_EN_CARD = 4;

/**
 * La miniatura de la card de Productos, UNA sola para los dos estados.
 *
 * Las de placeholder medían 42px con marco de borde y las de productos 44px con
 * `ring`, así que al elegir el primer producto las fotos cambiaban de tamaño y de
 * radio junto con la card. Con la misma clase, lo único que cambia entre un estado
 * y el otro es QUÉ imagen se ve (ADR-2026-08-25-card-de-productos-de-alto-fijo).
 *
 * `ring` y no `border`: el borde se come 2px de la imagen por lado (`box-sizing:
 * border-box`) y el anillo se dibuja por fuera, así que las dos miden lo mismo por
 * dentro y por fuera.
 */
const CLASE_MINIATURA_CARD =
  'w-[44px] h-[44px] rounded-xl ring-2 ring-white shadow-sm';

/**
 * Card cuadrada de Modelo / Pose: miniatura arriba, etiqueta y valor abajo.
 *
 * Las dos decisiones que definen la imagen van con foto porque son las únicas que no
 * se pueden resumir en una palabra —"Avery" no dice qué modelo es—, y porque son las
 * que el usuario cambia más seguido. El resto del panel son filas de texto.
 */
const CardPicker: React.FC<{
  etiqueta: string;
  valor: string;
  /**
   * Miniaturas de lo elegido. Varias = mosaico; vacío = placeholder.
   *
   * Acepta `null` como elemento: es UNA COSA ELEGIDA QUE NO TIENE FOTO —el modelo
   * "Automático", que la IA decide—. Se cuenta y ocupa su celda con el ícono de
   * automático, en vez de filtrarse: filtrado, la card decía "3 modelos" y mostraba dos
   * fotos, y no había manera de saber si faltaba una o si el tercero era el automático.
   */
  imagenes: (string | null)[];
  /** ¿Lo elegido es "que decida la IA"? Cambia el placeholder. */
  esAutomatico?: boolean;
  onClick: () => void;
  /**
   * Apilada: la card ocupa el ancho entero y su miniatura se mantiene CUADRADA.
   *
   * Es la forma que toma en MOBILE. En una fila de tres, repartidas en 390px, las
   * miniaturas quedaban de 109px y el nombre de lo elegido cortado; apiladas, la foto
   * —que es lo que se está eligiendo— se ve a tamaño de reconocerla.
   *
   * El `min-w-0` que la acompaña NO es decorativo: sin él, el `truncate` del valor no
   * achica nada para el reparto de la fila —el texto en una línea aporta su ancho
   * ENTERO como mínimo del flex item— y las dos cards de mobile dejaban de medir lo
   * mismo. Con "Trío relajado sentados en banco" (la pose de tres personas) el reparto
   * medido era 128px para Modelo contra 210px para Pose; con `min-w-0`, 169 y 169
   * (ADR-2026-09-04-las-cards-de-mobile-se-reparten-la-fila-en-partes-iguales).
   */
  horizontal?: boolean;
  /**
   * Alto fijo en px. Sin esto la card lo saca de su miniatura cuadrada, que a todo el
   * ancho se vuelve enorme: es el caso de Fondo en mobile.
   */
  alto?: number;
  /**
   * El valor puede ocupar DOS líneas y no una recortada.
   *
   * Es el caso del fondo descrito con palabras: en una línea de 13px, "un taller con luz
   * cálida entrando por la ventana" se corta en "un taller con luz…" y la card deja de
   * decir cuál es el fondo, que es lo único que tiene para decir.
   */
  valorEnDosLineas?: boolean;
  /** Qué dibuja la miniatura cuando no hay foto ni es automático. Default: "Sin elegir". */
  placeholder?: React.ReactNode;
  /**
   * Cómo se acomodan varias miniaturas.
   *
   *   • `grilla` (default) — mosaico 2×2. Es lo que quieren las poses: son muchas, y
   *     cada celda cuadrada muestra bien un encuadre.
   *   • `columnas` — una franja por elemento, lado a lado. Es lo de MODELO
   *     (ADR-2026-08-28-la-card-de-modelo-muestra-a-todos): son hasta tres y son fotos
   *     de cuerpo entero, y en la grilla 2×2 el tercero caía a la segunda fila y quedaba
   *     recortado a la mitad. En franjas verticales entran los tres enteros, que es
   *     además cómo van a salir en la foto final: uno al lado del otro.
   */
  mosaico?: 'grilla' | 'columnas';
  /**
   * Una tercera línea, más chica, debajo del valor: el detalle de lo elegido.
   *
   * Es el caso del fondo blanco, donde el valor ("Fondo blanco") nombra la opción y esto
   * dice cuál de sus variantes quedó ("Cálido · luz difusa").
   */
  subvalor?: string;
  /**
   * Chevron a la derecha, como las celdas de ajustes.
   *
   * Va con la forma de FILA (`alto`): ahí la card es un renglón —miniatura, texto y el
   * resto del ancho vacío—, y sin el chevron ese renglón no se distingue de un valor
   * mostrado. En la card cuadrada no hace falta: la foto grande ya se lee como algo que
   * se toca (ADR-2026-08-31-modelo-v2-cards-en-fila).
   */
  chevron?: boolean;
}> = ({
  etiqueta,
  valor,
  imagenes,
  esAutomatico,
  onClick,
  horizontal,
  alto,
  valorEnDosLineas,
  placeholder,
  subvalor,
  chevron,
  mosaico = 'grilla',
}) => (
  <button
    type="button"
    // Marca al disparador para que el click-afuera del panel lo ignore: sin esto el
    // `mousedown` lo cerraba y el `click` lo reabría, y el panel parpadeaba.
    data-abre-panel-lateral
    onClick={onClick}
    style={alto ? { height: alto } : undefined}
    className={`rounded-2xl bg-black/[0.035] hover:bg-black/[0.06] transition-colors p-3 ${
      alto
        ? 'w-full flex items-center gap-3 text-left'
        : `${horizontal ? 'w-full min-w-0' : 'flex-1 min-w-0'} flex flex-col items-center gap-2.5`
    }`}
  >
    <div
      // `relative`, y todo lo de adentro en `absolute inset-0`: con `place-items-center`
      // a secas el contenido NO se estira al recuadro —el `w-full h-full` de la foto y el
      // del mosaico no llegan a nada y el navegador los deja en su tamaño natural—, así
      // que la miniatura mostraba la imagen a medio recuadro, con una franja blanca al
      // lado. Es el mismo arreglo que ya lleva el `CardPicker` del Catálogo
      // (ADR-2026-09-10-la-miniatura-de-la-card-ancla-su-contenido).
      className={`relative rounded-xl overflow-hidden bg-white grid place-items-center ${
        // CUADRADO en las dos formas: con alto fijo, del alto de la card y a la
        // izquierda; sin él, del ancho entero. La foto se veía cortada por el anclaje
        // —arriba—, no por la forma del recuadro: probamos pasarlo a 9:16 y las dos
        // cards de mobile se volvían altísimas
        // (ADR-2026-09-11-la-miniatura-de-la-card-vuelve-a-ser-cuadrada).
        alto ? 'h-full aspect-square shrink-0' : 'w-full aspect-square'
      }`}
    >
      {imagenes.length > 1 && mosaico === 'columnas' ? (
        // Una franja por elemento, del alto entero de la miniatura. `object-top`: la
        // franja es angosta y lo que tiene que entrar sí o sí es la cara — centrado, con
        // tres, el recorte caía en el torso y las tres fotos se volvían indistinguibles.
        <div className="absolute inset-0 flex gap-px bg-black/[0.06]">
          {imagenes.slice(0, imagenes.length > 3 ? 2 : 3).map((url, i) =>
            url ? (
              <CachedImg
                key={`${url}-${i}`}
                src={url}
                alt=""
                className="flex-1 min-w-0 h-full object-cover object-top bg-white"
              />
            ) : (
              <span
                key={`auto-${i}`}
                className="flex-1 min-w-0 h-full grid place-items-center bg-white"
              >
                <Sparkles className="w-5 h-5 text-brand-black/25" />
              </span>
            ),
          )}
          {imagenes.length > 3 && (
            <span className="flex-1 min-w-0 grid place-items-center bg-white text-[13px] font-semibold text-brand-black/55">
              +{imagenes.length - 2}
            </span>
          )}
        </div>
      ) : imagenes.length > 1 ? (
        // Con varias elegidas la card las muestra TODAS —hasta cuatro, y la cuarta
        // celda pasa a ser el "+N" cuando hay más—: en este modo cada pose extra es una
        // imagen más que se genera y se paga, así que cuántas van es parte de lo que la
        // card tiene que decir. El valor de abajo dice el número; esto, cuáles.
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-px bg-black/[0.06]">
          {imagenes.slice(0, imagenes.length > 4 ? 3 : 4).map((url, i) =>
            url ? (
              <CachedImg
                key={`${url}-${i}`}
                src={url}
                alt=""
                className="min-w-0 min-h-0 w-full h-full object-cover bg-white"
              />
            ) : (
              <span
                key={`auto-${i}`}
                className="min-w-0 min-h-0 w-full h-full grid place-items-center bg-white"
              >
                <Sparkles className="w-5 h-5 text-brand-black/25" />
              </span>
            ),
          )}
          {imagenes.length > 4 && (
            <span className="grid place-items-center bg-white text-[13px] font-semibold text-brand-black/55">
              +{imagenes.length - 3}
            </span>
          )}
        </div>
      ) : imagenes.length === 1 && imagenes[0] ? (
        <CachedImg
          src={imagenes[0]}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : esAutomatico ? (
        // "Automático" NO es un hueco: es una decisión tomada —la IA elige—, así que la
        // card no puede decir "Sin elegir" arriba y "Automático" abajo, que es lo que
        // hacía.
        <Sparkles className="w-6 h-6 text-brand-black/25" />
      ) : (
        placeholder ?? (
          <span className="text-[12px] text-brand-black/35">Sin elegir</span>
        )
      )}
    </div>
    {/* `w-full` en la card cuadrada: el contenedor es un `flex flex-col items-center`, y
        ahí un hijo bloque mide SU CONTENIDO, no el ancho de la card. Sin esto el
        `truncate` de abajo no tenía contra qué recortar y un valor largo —"Trío relajado
        sentados en banco"— se salía de la card en vez de elipsearse
        (ADR-2026-09-04-el-valor-de-la-card-se-elipsea-de-verdad). `min-w-0` no alcanza:
        no pone tope, sólo saca el piso. */}
    <div
      className={alto ? 'min-w-0 flex-1' : 'w-full min-w-0 text-center'}
    >
      <p className="text-[14px] font-bold text-brand-black/90 leading-tight">
        {etiqueta}
      </p>
      <p
        className={`text-[13px] text-brand-black/50 leading-tight mt-0.5 ${
          valorEnDosLineas ? 'line-clamp-2' : 'truncate'
        }`}
      >
        {valor}
      </p>
      {subvalor && (
        <p className="text-[12px] text-brand-black/35 leading-tight mt-0.5 truncate">
          {subvalor}
        </p>
      )}
    </div>
    {chevron && (
      <ChevronRight className="w-[18px] h-[18px] shrink-0 text-brand-black/30" />
    )}
  </button>
);


/**
 * La card de Productos: preview de lo elegido y disparador del picker.
 *
 * Vive afuera de `PanelCompacto` porque MOBILE la monta sola —ahí no hay columna de
 * configuración, sólo esta card debajo de la imagen del modo— mientras que en desktop es
 * la primera fila del panel. Ver ADR-2026-08-23-modelo-v2-mobile.
 */
export const CardProductos: React.FC<{
  products: MockProductV2[];
  onAbrirProductos: () => void;
  onSubirArchivos: (files: FileList | File[]) => void;
  /**
   * Las tres copys y las ilustraciones de la card son de MODA por defecto ("outfit", la
   * remera/el jean/la zapatilla). Un modo de otro rubro las pasa acá: es lo que hace
   * "Mostrar cómo funciona", que es de muebles y elige UN producto
   * (ADR-2026-09-03-modo-mostrar-como-funciona). Opcionales para no tocar a Modelo.
   */
  copyVacio?: string;
  /** La acción en azul debajo del nombre, con productos elegidos. */
  copyElegido?: string;
  /**
   * El ícono de esa acción cuando viene `copyElegido`: lápiz por defecto —los modos que
   * la reemplazan "editan" o "cambian" algo—, o el "+" cuando el copy es de AGREGAR.
   * Automática pide "Añadir producto" con cualquier cantidad
   * (ADR-2026-09-23-automatico-la-card-dice-anadir-producto): ahí la escena la arma la
   * IA y lo que se le suma son productos, no un outfit que se edita.
   */
  iconoElegido?: 'editar' | 'agregar';
  /**
   * Miniaturas del estado vacío, en el orden del abanico. SIN pasar nada, la card las
   * saca sola del contexto (los últimos tres productos del usuario), que es lo que
   * corresponde en Modelo; un modo de otro rubro puede elegir qué mostrar ahí —
   * "Ambiente completo" manda los últimos productos cargados para que la pantalla de
   * arranque muestre que la biblioteca tiene algo adentro
   * (ADR-2026-09-10-la-card-vacia-muestra-los-ultimos-productos).
   */
  placeholders?: string[];
}> = ({
  products,
  onAbrirProductos,
  onSubirArchivos,
  copyVacio = 'Elegí tus productos',
  copyElegido,
  iconoElegido = 'editar',
  placeholders,
}) => {
  const [arrastrando, setArrastrando] = React.useState(false);
  /**
   * Las tres miniaturas del estado VACÍO: los últimos tres productos del usuario —
   * ADR-2026-09-04-la-card-vacia-muestra-productos-de-verdad.
   *
   * Salen del `ProductsContext` y no de una prop: los cuatro puntos de montaje de esta
   * card (la columna de desktop, el paso 1 de mobile, el pie de mobile y el panel del
   * Catálogo) tendrían que pasar la misma lista, y el contexto ya la tiene cargada —es
   * de donde la lee la grilla que abre esta card—.
   *
   * "Los últimos" = los de `createdAt` más reciente. Sin esa fecha —productos viejos o
   * de demo— se respeta el orden en que vino la lista, que es el que muestra la grilla.
   */
  const productosDelUsuario = useUserProducts();
  const ultimosTres = React.useMemo(() => {
    const conFoto = productosDelUsuario.filter((p) => !!p.imageUrl);
    const ordenados = [...conFoto].sort((a, b) => {
      if (!a.createdAt && !b.createdAt) return 0;
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return b.createdAt.localeCompare(a.createdAt);
    });
    return ordenados.slice(0, 3);
  }, [productosDelUsuario]);
  /**
   * Lo que se pinta en el abanico de la card VACÍA, en orden de prioridad: lo que manda
   * el modo, los últimos del usuario, las ilustraciones de Moda.
   *
   * Las tres son la misma idea —que el hueco muestre algo real de la cuenta— resuelta
   * en dos lugares distintos: acá adentro para los cuatro puntos de montaje de Modelo,
   * que no tendrían por qué pasar la misma lista, y por prop para los modos de otro
   * rubro, que sí eligen. La prop manda cuando está.
   */
  const miniaturasVacias = React.useMemo(() => {
    if (placeholders && placeholders.length > 0) {
      return placeholders.map((src) => ({ key: src, src }));
    }
    if (ultimosTres.length > 0) {
      return ultimosTres.map((p) => ({ key: String(p.id), src: p.imageUrl }));
    }
    return PLACEHOLDERS_PRODUCTO.map((src) => ({ key: src, src }));
  }, [placeholders, ultimosTres]);
  // La card tiene la MISMA forma que las de Modelo y Pose: fondo gris, hover y el
  // chevron de "esto abre algo". El recuadro punteado que había antes decía "soltá un
  // archivo acá" —que es una de las dos cosas que hace— y no se leía como un control que
  // se toca, aunque tocarlo fuera el camino principal.
  //
  // Sin encabezado propio: ya dice "Elegí tus productos" adentro, en el mismo peso que
  // tenía el título de arriba, que sólo repetía la palabra.
  //
  // Sigue aceptando archivos soltados encima: el `onDrop` vive en la card, así que
  // arrastrar funciona igual sin que el borde punteado tenga que anunciarlo.
  return (
    <div>
      <button
        type="button"
        data-abre-panel-lateral
        onClick={onAbrirProductos}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => {
          e.preventDefault();
          setArrastrando(false);
          if (e.dataTransfer.files?.length) onSubirArchivos(e.dataTransfer.files);
        }}
        // Alto FIJO, el mismo con productos y sin ellos. Antes eran 64px vacía y
        // 72px llena: elegir el primer producto empujaba 8px hacia abajo a Modelo,
        // Pose, Fondo y todo lo que sigue, y el salto se leía como si la columna se
        // hubiera reacomodado sola. 72px es la medida de las dos, porque es la que
        // necesita el estado lleno para sus dos líneas de texto
        // (ADR-2026-08-25-card-de-productos-de-alto-fijo).
        className={`w-full h-[72px] rounded-2xl px-3 flex items-center gap-3 transition-colors text-left ${
          arrastrando
            ? 'bg-brand-primary/[0.06] ring-2 ring-brand-primary/40'
            : 'bg-black/[0.035] hover:bg-black/[0.06]'
        }`}
      >
        {products.length > 0 ? (
          <>
            {/* Preview de lo elegido: las últimas cuatro, y la cuarta celda pasa a ser
                el "+N" cuando hay más. `slice(-4).reverse()`: lo último que se sumó va
                primero, que es lo que el usuario acaba de tocar y lo que espera ver. */}
            {/* Apiladas y solapadas, no en fila separada: el outfit se lee como UN
                conjunto —lo que va junto en la foto— y no como una lista de cosas
                sueltas. El marco blanco de cada una las despega de la de atrás, y el
                `+N` cierra la pila cuando hay más de las que entran. */}
            <span className="flex shrink-0 items-center -space-x-3">
              {products
                .slice(-PREVIEWS_EN_CARD)
                .reverse()
                .slice(
                  0,
                  products.length > PREVIEWS_EN_CARD
                    ? PREVIEWS_EN_CARD - 1
                    : PREVIEWS_EN_CARD,
                )
                .map((p, i) => (
                  <MiniaturaProducto
                    key={p.id}
                    producto={p}
                    // `zIndex` decreciente: la primera —la última que se sumó— queda
                    // ARRIBA de las que le siguen, así el orden se lee de adelante hacia
                    // atrás como una pila real.
                    style={{ zIndex: PREVIEWS_EN_CARD - i }}
                    className={`${CLASE_MINIATURA_CARD} relative`}
                  />
                ))}
              {products.length > PREVIEWS_EN_CARD && (
                <span
                  className={`${CLASE_MINIATURA_CARD} relative bg-neutral-100 grid place-items-center text-[13px] font-semibold text-brand-black/55`}
                >
                  +{products.length - (PREVIEWS_EN_CARD - 1)}
                </span>
              )}
            </span>
            <span className="min-w-0 flex-1 flex flex-col justify-center">
              {/* Los NOMBRES, no la cuenta: "3 productos" no dice qué hay puesto, y con
                  varios elegidos lo que se está armando es un outfit concreto. En bold
                  porque son el contenido de la card.

                  UN renglón en las dos superficies
                  (ADR-2026-09-04-la-card-de-productos-vuelve-a-un-renglon): con dos, la
                  lista de nombres se leía como un párrafo y empujaba el alto de la card a
                  88px para mostrar dos líneas que igual terminan en elipsis. */}
              <span className="text-[14px] font-bold text-brand-black/90 truncate">
                {products.map((p) => p.name).join(', ')}
              </span>
            </span>
            {/* MOBILE: el chevron, igual que en la card vacía —las dos abren lo mismo y
                así se leen como el mismo control—. La acción con su etiqueta vive en el
                botón ancho de abajo, fuera de la card. */}
            <ChevronRight className="w-4 h-4 shrink-0 text-brand-black/30 md:hidden" />
            {/* DESKTOP: la acción vuelve ADENTRO de la card, al fondo a la derecha
                (ADR-2026-09-04-en-desktop-el-boton-de-outfit-vuelve-a-la-card). Acá la
                columna es ancha: la pastilla entra sin comerse el nombre, que además
                tiene dos renglones. En el teléfono no —eso es lo que la había mandado
                abajo (ADR-2026-09-04-el-boton-de-outfit-va-debajo-de-la-card)—.

                `span` y no `<button>`: la card ENTERA ya es el botón que abre la
                biblioteca y anidar botones es HTML inválido. Como hace exactamente lo
                mismo que la card, alcanza con que lo anuncie. */}
            <span className="ml-auto hidden md:inline-flex h-9 shrink-0 items-center gap-1.5 rounded-xl border border-brand-primary bg-[#EBEFFE] px-3 text-[13px] font-semibold text-brand-primary">
              {copyElegido ? (
                <>
                  {iconoElegido === 'agregar' ? (
                    <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                  ) : (
                    <Pencil className="w-3.5 h-3.5" />
                  )}
                  {copyElegido}
                </>
              ) : products.length > 1 ? (
                <>
                  <Pencil className="w-3.5 h-3.5" />
                  Editar outfit
                </>
              ) : (
                <>
                  <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                  Añadir producto
                </>
              )}
            </span>
          </>
        ) : (
          <>
            {/* Fotos de VERDAD y no ilustraciones —
                ADR-2026-09-04-la-card-vacia-muestra-productos-de-verdad,
                ADR-2026-09-10-la-card-vacia-muestra-los-ultimos-productos.

                Acá iban ilustraciones fijas, a propósito: la idea era que se leyeran
                como un ejemplo ("tus productos van acá") y no como algo ya elegido. Se
                pidió mostrar los reales, que además dicen algo cierto —que del otro
                lado hay catálogo, y cuál—. Lo que evita que parezcan elegidos sigue
                siendo el texto de al lado, que pide elegir, y la ausencia de cualquier
                marca de selección.

                Tres fuentes, en orden: las que manda el modo por `placeholders` —un
                modo de otro rubro elige qué mostrar—, si no los últimos del usuario que
                la card saca sola del contexto, y si no las ilustraciones. Sin productos
                todavía (cuenta nueva, o el contexto sin cargar) caen las ilustraciones:
                la card no puede quedarse sin nada del lado izquierdo, que es lo que le
                da su forma y su alto.

                Inclinadas y con marco blanco: se leen como fotos apoyadas una al lado de
                la otra, no como celdas de la grilla. La rotación va en sentidos opuestos
                —una a cada lado— y el `-space-x-2` las deja solaparse un poco, que es lo
                que las hace ver apiladas y no simplemente torcidas. El contenedor NO
                recorta (sin `overflow-hidden`): al rotar, la caja que ocupa cada una
                crece y se comería las esquinas. */}
            <span className="flex shrink-0 items-center -space-x-2">
              {miniaturasVacias.map((miniatura, i, todas) => (
                <span
                  key={miniatura.key}
                  className={`${CLASE_MINIATURA_CARD} overflow-hidden bg-white block`}
                  style={{
                    // Abanico: la primera se inclina a la izquierda, la última a la
                    // derecha y las del medio quedan derechas. Repartir el ángulo por
                    // posición —y no alternarlo— es lo que las hace ver apoyadas una
                    // sobre otra; alternando, tres o más quedaban en zigzag.
                    transform: `rotate(${
                      ((i / Math.max(1, todas.length - 1)) * 2 - 1) *
                      ANGULO_PLACEHOLDER
                    }deg)`,
                  }}
                >
                  <CachedImg
                    src={miniatura.src}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </span>
              ))}
            </span>
            <span className="min-w-0 flex-1 flex items-center gap-2 text-[14.5px]">
              {/* Bold y en el tono de los otros títulos de la columna: es la etiqueta
                  de la card, igual que "Modelo" o "Calidad", no un texto de ayuda. */}
              <span className="font-bold text-brand-black/90 truncate">
                {copyVacio}
              </span>
            </span>
            <ChevronRight className="w-4 h-4 shrink-0 text-brand-black/30" />
          </>
        )}
      </button>
      {/* ── La acción del outfit, EN MOBILE ─────────────────────────────────────
          En desktop vive adentro de la card, al fondo a la derecha — acá abajo sólo en el
          teléfono (ADR-2026-09-04-en-desktop-el-boton-de-outfit-vuelve-a-la-card).

          ADR-2026-09-04-el-boton-de-outfit-va-debajo-de-la-card (supersede
          ADR-2026-09-04-editar-outfit-es-una-pastilla-al-final-de-la-card y
          ADR-2026-09-04-el-boton-de-outfit-es-solo-el-icono).

          Empezó como un renglón azul debajo del nombre —se leía como subtítulo del
          producto—, pasó a una pastilla a la derecha DENTRO de la card y ahí se comió
          150px de una card que en un teléfono mide ~330: el nombre quedaba cortado a
          cuatro letras. Con sólo el ícono el nombre entraba pero se perdía la palabra.

          Fuera de la card las tres cosas conviven: el nombre usa el ancho entero, el
          botón dice qué hace y la card conserva sus 72px fijos
          (ADR-2026-08-25-card-de-productos-de-alto-fijo). Es además la anatomía que el
          panel de desktop ya usaba para esta misma acción: un botón ancho debajo de los
          productos.

          Va en el azul secundario —borde de marca sobre `#EBEFFE`— y no en blanco: es una
          acción sobre lo que la card muestra, no un control neutro más de la columna.

          Sólo con productos elegidos: en la card vacía la acción es elegir el primero, y
          eso ya lo hace la card entera. */}
      {products.length > 0 && (
        <button
          type="button"
          data-abre-panel-lateral
          onClick={onAbrirProductos}
          // Sin borde: el relleno azul claro ya lo despega de la card, y el contorno
          // sumaba una segunda línea al lado del borde de la card de productos.
          className="md:hidden mt-2 w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-2xl bg-[#EBEFFE] px-3 text-[13.5px] font-semibold text-brand-primary transition-colors hover:bg-[#DFE6FD] active:scale-[0.99]"
        >
          {/* Mismo copy que la pastilla de desktop: es la misma acción y no puede
              llamarse distinto según el ancho. Un modo de otro rubro lo reemplaza por
              `copyElegido` —"outfit" no existe en muebles—. */}
          {copyElegido ? (
            <>
              {iconoElegido === 'agregar' ? (
                <Plus className="w-4 h-4" strokeWidth={2.5} />
              ) : (
                <Pencil className="w-4 h-4" />
              )}
              {copyElegido}
            </>
          ) : products.length > 1 ? (
            <>
              <Pencil className="w-4 h-4" />
              Editar outfit
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              Añadir producto al outfit
            </>
          )}
        </button>
      )}
    </div>
  );
};


/**
 * La foto de un modelo, buscada FRESCA en `userModels`.
 *
 * La `imageUrl` que viaja en la selección es una signed URL que caduca —la selección se
 * restaura de `localStorage` y puede tener horas—, así que la de `userModels` manda y la
 * de la selección queda de respaldo. `auto` y `custom` no tienen foto.
 */
const imagenDeModelo = (
  sel: ModelSelection | null,
  userModels: ModelV2[],
): string | null => {
  if (!sel) return null;
  if (sel.mode === 'preset') {
    const m = userModels.find((u) => String(u.id) === sel.id);
    return m?.thumbUrl ?? m?.thumbnailUrl ?? sel.imageUrl ?? null;
  }
  if (sel.mode === 'upload') return sel.previewUrl;
  return null;
};

/**
 * Quién lleva qué, cuando la foto es de varios modelos.
 *
 * En v2 elegir varios modelos es pedir UNA foto con todos adentro
 * (ADR-2026-08-25-en-v2-los-modelos-van-en-la-misma-foto), así que la pregunta que
 * aparece es cuál de ellos usa cada producto. La card de Modelo sola no puede
 * contestarla: dice "3 modelos" y nada más.
 *
 * Cada fila abre el picker de productos DE ESE modelo. Sin asignación propia, el modelo
 * hereda los productos elegidos para toda la foto — que es lo que hace el payload
 * (`modelo_config[idx]` cae al `variantIds` global), así que la fila muestra lo mismo que
 * se va a generar y no una promesa aparte.
 */
const QuienLlevaQueV2: React.FC<{
  modelos: ModelSelection[];
  userModels: ModelV2[];
  productosPorModelo: Record<number, MockProductV2[]>;
  productosDefault: MockProductV2[];
  onTapModelo: (idx: number) => void;
}> = ({
  modelos,
  userModels,
  productosPorModelo,
  productosDefault,
  onTapModelo,
}) => (
  // El aire de adentro es el mismo que el de las cards de arriba (`p-3`), no el `p-1.5`
  // que tenía: pegado a los bordes, el bloque se leía como una lista metida a presión y
  // no como una card más de la columna. Y el título se DESPEGA de las filas (`mb-1.5`
  // sobre el `gap-0.5` de la lista): con la misma separación que hay entre dos filas,
  // "Qué lleva cada modelo" parecía la primera de ellas en vez de encabezarlas.
  <div className="rounded-2xl bg-black/[0.035] p-3 flex flex-col gap-0.5">
    <p className="px-2 mb-1.5 text-[11px] font-bold uppercase tracking-wide text-brand-black/45 leading-none">
      Qué lleva cada {palabraDeModeloActual().singular}
    </p>
    {modelos.map((sel, idx) => {
      const productos = productosPorModelo[idx] ?? productosDefault;
      const foto = imagenDeModelo(sel, userModels);
      return (
        <button
          key={`${sel.mode}-${idx}`}
          type="button"
          // Como las cards de la columna: el panel lateral no cuenta este tap como
          // "click afuera", así que volver a tocar la misma fila lo CIERRA en vez de
          // cerrarlo y reabrirlo (ADR-2026-08-24-la-card-abre-y-cierra-su-panel).
          data-abre-panel-lateral
          onClick={() => onTapModelo(idx)}
          className="w-full flex items-center gap-2.5 rounded-xl px-2 py-1.5 hover:bg-white transition-colors text-left"
        >
          <span className="w-9 h-9 rounded-lg overflow-hidden bg-white grid place-items-center shrink-0">
            {foto ? (
              <CachedImg
                src={foto}
                alt=""
                className="w-full h-full object-cover object-top"
              />
            ) : (
              <Sparkles className="w-4 h-4 text-brand-black/25" />
            )}
          </span>
          <span className="flex-1 min-w-0 text-[13.5px] text-brand-black/90 truncate">
            {modelSelectionLabel(sel)}
          </span>
          {productos.length === 0 ? (
            <span className="text-[12.5px] text-brand-black/35 shrink-0">
              Sin productos
            </span>
          ) : (
            // Solapadas y hasta cuatro: es la misma forma que tiene la card de
            // Productos, así que las dos se leen como el mismo dato.
            <span className="flex items-center -space-x-1.5 shrink-0">
              {productos.slice(0, productos.length > 4 ? 3 : 4).map((p) => (
                <CachedImg
                  key={p.id}
                  src={p.imageUrl}
                  alt=""
                  className="w-7 h-7 rounded-md object-cover bg-white ring-2 ring-white"
                />
              ))}
              {productos.length > 4 && (
                <span className="w-7 h-7 rounded-md bg-white ring-2 ring-white grid place-items-center text-[11px] font-semibold text-brand-black/55">
                  +{productos.length - 3}
                </span>
              )}
            </span>
          )}
          <ChevronRight className="w-4 h-4 shrink-0 text-brand-black/30" />
        </button>
      );
    })}
  </div>
);

/**
 * Las cards de Modelo y Pose.
 *
 * Igual que `CardProductos`, vive afuera del panel porque MOBILE las monta sueltas —en
 * su pantalla no hay columna de configuración— y desktop las usa como una fila más.
 * Ver ADR-2026-08-24-modelo-v2-mobile-modelo-y-pose.
 */
export const CardsModeloPose: React.FC<{
  modelSelection: ModelSelection | null;
  userModels: ModelV2[];
  onOpenModelPicker: () => void;
  allPoses: PoseV2[];
  customPoses: PoseV2[];
  poseIds: string[];
  onOpenPosePicker: () => void;
  /**
   * Fondo como TERCERA card.
   *
   * Opcional porque sólo lo pide mobile: en desktop el fondo vive en la lista de
   * ajustes de la columna, y dibujarlo también acá sería el mismo control dos veces
   * en la misma pantalla.
   */
  fondoSelection?: FondoSelection | null;
  onOpenFondoPicker?: () => void;
  /**
   * Cuántos modelos hay elegidos en total (el principal + los demás).
   *
   * Con 2 o 3, la card no puede decir el nombre del primero: en v2 elegir varios
   * arma UNA foto con todos adentro, así que "Automático" mientras hay tres
   * elegidos es directamente falso
   * (ADR-2026-08-25-en-v2-los-modelos-van-en-la-misma-foto). Default 1 para los
   * call-sites de selección única.
   */
  modelosElegidos?: number;
  /**
   * El grupo entero, en el orden en que viaja al payload — ver `QuienLlevaQueV2`.
   *
   * Con dos o más, debajo de las cards aparece la lista de quién lleva qué. Con uno solo
   * no se dibuja: la card de Productos ya dice lo mismo y sin ambigüedad.
   */
  modelosGrupo?: ModelSelection[];
  /** Productos asignados a un modelo puntual (idx = posición en `modelosGrupo`). */
  productosPorModelo?: Record<number, MockProductV2[]>;
  /** Los productos de la foto: lo que lleva un modelo sin asignación propia. */
  productosDefault?: MockProductV2[];
  /** Tap en una fila de "Qué lleva cada modelo" — abre el picker de ese modelo. */
  onTapProductosModelo?: (idx: number) => void;
  /** Apiladas y a todo el ancho — ver `horizontal` en `CardPicker`. */
  apiladas?: boolean;
  /**
   * Moodboard como cuarta card, debajo de Fondo: una carpeta de Mi marca, entera o con
   * algunas fotos (ADR-2026-10-18-moodboard-en-el-modo-modelo). Sólo la pasa la columna
   * de escritorio de las marcas que ven moodboards: en el teléfono el modo no tiene panel
   * lateral. Por ahora SÓLO SE VE: no viaja a la generación.
   */
  moodboard?: SeleccionDeMoodboard | null;
  onOpenMoodboard?: () => void;
}> = ({
  modelSelection,
  userModels,
  modelosElegidos = 1,
  modelosGrupo = [],
  productosPorModelo = {},
  productosDefault = [],
  onTapProductosModelo,
  onOpenModelPicker,
  allPoses,
  customPoses,
  poseIds,
  onOpenPosePicker,
  fondoSelection,
  onOpenFondoPicker,
  apiladas = false,
  moodboard = null,
  onOpenMoodboard,
}) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  const posesElegidas = React.useMemo(() => {
    const todas = [...allPoses, ...customPoses];
    return poseIds
      .map((id) => todas.find((p) => p.id === id))
      .filter((p): p is PoseV2 => !!p);
  }, [allPoses, customPoses, poseIds]);

  // Sin pose elegida el rótulo ya NO dice "Automática": el modo arranca con la
  // primera recomendada puesta y no ofrece esa opción
  // (ADR-2026-09-04-el-modo-modelo-siempre-arranca-con-pose). El cero sigue siendo
  // alcanzable por un instante —mientras el catálogo carga— y ahí el rótulo dice lo
  // único cierto: que todavía no hay ninguna.
  const poseLabel =
    posesElegidas.length === 0
      ? 'Elegí una pose'
      : posesElegidas.length === 1
        ? posesElegidas[0].nombre
        : `${posesElegidas.length} elegidas`;

  const modeloLabel =
    modelosElegidos > 1
      ? `${modelosElegidos} ${palabra.plural}`
      : modelSelection
        ? modelSelectionLabel(modelSelection)
        : 'Automático';

  /**
   * Las fotos de la card de Modelo: UNA POR MODELO ELEGIDO
   * (ADR-2026-08-28-la-card-de-modelo-muestra-a-todos).
   *
   * Con varios elegidos la card mostraba sólo la del primero y "3 modelos" abajo: la
   * foto contradecía al texto —se veía UN modelo— y no había forma de ver a quiénes se
   * había elegido sin volver a abrir el panel. `CardPicker` ya sabe armar el mosaico;
   * lo que faltaba era pasarle la lista entera, como ya hacía la card de Pose.
   *
   * Sale de `modelosGrupo`, que es el orden del payload; con uno solo (o cuando el
   * caller no lo manda — mobile no lo pasaba) cae en `modelSelection`. Los `auto` y los
   * `custom` no tienen foto: se filtran, y si no queda ninguna la card cae en su
   * placeholder de siempre.
   */
  const modeloImagenes = React.useMemo(() => {
    const base = modelosGrupo.length > 0 ? modelosGrupo : [modelSelection];
    // Sin filtrar los que no tienen foto: "Automático" es un modelo elegido y ocupa su
    // franja con el ícono, así que la cantidad de celdas siempre coincide con el
    // "N modelos" de abajo. Con UNO solo y sin foto queda `[null]`, que `CardPicker`
    // resuelve con su placeholder de siempre.
    return base.map((sel) => imagenDeModelo(sel, userModels));
  }, [modelosGrupo, modelSelection, userModels]);

  /**
   * Alto de la card en forma de FILA (desktop).
   *
   * 76px: la miniatura cuadrada de ese alto alcanza para reconocer un modelo o una pose,
   * y las tres cards juntas miden lo que medía una sola fila de cuadradas — que es lo que
   * libera el alto para que los ajustes y el Generar entren fijos abajo
   * (ADR-2026-08-31-modelo-v2-cards-en-fila).
   */
  const ALTO_FILA = 76;

  // Modelo, Pose y Fondo en COLUMNA. En desktop las tres son filas —miniatura a la
  // izquierda, etiqueta y valor al lado, chevron a la derecha—; en mobile (`apiladas`)
  // Modelo y Pose siguen compartiendo la fila de arriba con su foto grande y Fondo va
  // debajo, que es el orden de ADR-2026-08-24-fondo-es-una-card-mas.
  return (
    <div className="flex flex-col gap-3">
      {apiladas ? (
        // Mobile: las dos cards cuadradas comparten fila y cada una se lleva media
        // columna. Su foto es el contenido, no un ícono al costado.
        <div className="flex gap-3">
          <CardPicker
            horizontal
            etiqueta={palabra.titulo}
            valor={modeloLabel}
            imagenes={modeloImagenes}
            mosaico="columnas"
            esAutomatico={!modelSelection || modelSelection.mode === 'auto'}
            onClick={onOpenModelPicker}
          />
          <CardPicker
            horizontal
            etiqueta="Pose"
            valor={poseLabel}
            imagenes={posesElegidas.map((p) => p.imageUrl)}
            esAutomatico={posesElegidas.length === 0}
            onClick={onOpenPosePicker}
          />
        </div>
      ) : (
        // Desktop: una fila cada una, del mismo alto, apiladas como una lista de
        // decisiones. Reemplaza a la fila de dos cuadradas
        // (ADR-2026-08-31-modelo-v2-cards-en-fila).
        <>
          <CardPicker
            alto={ALTO_FILA}
            chevron
            etiqueta={palabra.titulo}
            valor={modeloLabel}
            imagenes={modeloImagenes}
            mosaico="columnas"
            esAutomatico={!modelSelection || modelSelection.mode === 'auto'}
            onClick={onOpenModelPicker}
          />
          <CardPicker
            alto={ALTO_FILA}
            chevron
            etiqueta="Pose"
            valor={poseLabel}
            imagenes={posesElegidas.map((p) => p.imageUrl)}
            esAutomatico={posesElegidas.length === 0}
            onClick={onOpenPosePicker}
          />
        </>
      )}

      {/* Justo DEBAJO de la card de Modelo, no al pie de la columna: es la vuelta de
          página de esa card —qué implica haber elegido varios— y lejos de ella habría que
          adivinar de qué está hablando. */}
      {modelosGrupo.length > 1 && onTapProductosModelo && (
        <QuienLlevaQueV2
          modelos={modelosGrupo}
          userModels={userModels}
          productosPorModelo={productosPorModelo}
          productosDefault={productosDefault}
          onTapModelo={onTapProductosModelo}
        />
      )}
      {onOpenFondoPicker && (
        <CardPicker
          horizontal
          // 120px en mobile: a todo el ancho y con la miniatura cuadrada la card se iba a
          // 396px —el fondo terminaba pesando más que el modelo y la pose juntos, que son
          // las dos decisiones de arriba—. En desktop mide lo mismo que las otras dos
          // filas: ahí las tres son renglones y el fondo no tiene por qué ser más alto.
          alto={apiladas ? 120 : ALTO_FILA}
          chevron={!apiladas}
          etiqueta="Fondo"
          // Con el fondo descrito con palabras, el valor es EL TEXTO y no la etiqueta
          // "Descripción": la card existe para decir con qué se va a generar, y
          // "Descripción" dice el mecanismo, no el fondo. El resto de los modos siguen
          // con el label de siempre.
          valor={
            fondoSelection?.mode === 'descripcion' && fondoSelection.text.trim()
              ? fondoSelection.text.trim()
              : fondoSelectionLabel(fondoSelection ?? null)
          }
          // El blanco elegido son SEIS: tres tonos por dos luces. Con "Fondo blanco" a
          // secas, cambiar el tono no cambiaba nada de lo que la card mostraba y no había
          // dónde ver qué había quedado sin volver a abrir el panel.
          subvalor={
            fondoSelection?.mode === 'blanco'
              ? fondoBlancoAjustesLabel(fondoSelection)
              : undefined
          }
          valorEnDosLineas={fondoSelection?.mode === 'descripcion'}
          // Un fondo descrito SÍ está elegido: la miniatura muestra la "T" de escribir en
          // vez del "Sin elegir", que decía lo contrario de lo que pasó. Con el blanco
          // pasaba igual —la card decía "Fondo blanco" abajo y "Sin elegir" arriba—, y
          // ahí ni siquiera hace falta un ícono: el recuadro blanco vacío ES la preview
          // literal de lo elegido, que es lo que ya hace la card del Catálogo
          // (`vacioFondo` en `PanelCatalogoCompacto`).
          placeholder={
            fondoSelection?.mode === 'descripcion' ? (
              <TypeIcon className="w-6 h-6 text-brand-black/25" />
            ) : fondoSelection?.mode === 'blanco' ? (
              <span className="w-full h-full bg-white" />
            ) : undefined
          }
          // Sólo los fondos de biblioteca y los guardados tienen foto. "Automático",
          // "Fondo blanco" y un fondo descrito con palabras no tienen ninguna imagen
          // que mostrar — ahí la card cae en su placeholder.
          imagenes={
            fondoSelection?.mode === 'biblioteca' ||
            fondoSelection?.mode === 'guardado'
              ? [fondoSelection.imageUrl]
              : []
          }
          esAutomatico={!fondoSelection || fondoSelection.mode === 'auto'}
          onClick={onOpenFondoPicker}
        />
      )}
      {onOpenMoodboard && (
        <CardPicker
          horizontal
          alto={ALTO_FILA}
          chevron
          etiqueta="Estilo"
          valor={moodboard ? moodboard.nombre : 'Sin estilo'}
          subvalor={moodboard ? detalleDeSeleccion(moodboard) : undefined}
          // Hasta cuatro de lo elegido: el mosaico de 2×2, como las poses.
          imagenes={moodboard?.miniaturas ?? []}
          placeholder={<Palette className="w-6 h-6 text-brand-black/25" />}
          onClick={onOpenMoodboard}
        />
      )}
    </div>
  );
};

export const PanelCompacto: React.FC<PanelCompactoProps> = ({
  products,
  onAbrirProductos,
  onSubirArchivos,
  modelSelection,
  onOpenModelPicker,
  modelosElegidos = 1,
  modelosGrupo,
  productosPorModelo,
  onTapProductosModelo,
  userModels,
  allPoses,
  customPoses,
  poseIds,
  onOpenPosePicker,
  calidad,
  onCalidadChange,
  cantidad,
  cantidadMax,
  onCantidadChange,
  contadorVisible = true,
  fondoSelection,
  onOpenFondo,
  moodboard,
  onOpenMoodboard,
  formatoLabel,
  onOpenFormato,
  otraConfig,
  onOtraChange,
  cantidadImagenes,
  creditCost,
  onGenerate,
  falta,
}) => {
  return (
    /**
     * La columna en DOS piezas: lo que scrollea y un pie fijo.
     *
     * Los ajustes (Calidad · Formato), la cantidad y el Generar viven abajo y no se van
     * con el scroll: son el final del recorrido y lo que se toca en cada tanda, y con la
     * columna crecida —tres cards, la instrucción, la lista de quién lleva qué— quedaban
     * fuera de la pantalla, así que generar pedía scrollear hasta el fondo cada vez
     * (ADR-2026-08-31-modelo-v2-ajustes-y-generar-fijos-abajo).
     *
     * `h-full min-h-0`: el alto lo pone el contenedor de afuera, que es quien sabe cuánto
     * mide la columna. Sin `min-h-0` el scroller de adentro no puede achicarse y el pie
     * se empuja fuera de la vista, que es el bug clásico de flex + overflow.
     */
    <div className="h-full min-h-0 flex flex-col">
      {/* Lo que scrollea. El `pb-4` es el aire entre la instrucción y el pie. */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 pb-4">
      <CardProductos
        products={products}
        onAbrirProductos={onAbrirProductos}
        onSubirArchivos={onSubirArchivos}
      />

      {/* ── Modelo y Pose ──────────────────────────────────────────────── */}
      <CardsModeloPose
        modelosGrupo={modelosGrupo}
        productosPorModelo={productosPorModelo}
        // Los productos de la foto: lo que lleva el modelo que no tiene asignación
        // propia. Son los mismos que muestra la card de Productos de arriba.
        productosDefault={products}
        onTapProductosModelo={onTapProductosModelo}
        modelSelection={modelSelection}
        userModels={userModels}
        modelosElegidos={modelosElegidos}
        onOpenModelPicker={onOpenModelPicker}
        allPoses={allPoses}
        customPoses={customPoses}
        poseIds={poseIds}
        onOpenPosePicker={onOpenPosePicker}
        // Fondo dejó de ser una fila de ajustes y es una card más: es una decisión con
        // FOTO —como el modelo y la pose— y no un valor que se resuma en una palabra.
        fondoSelection={fondoSelection}
        onOpenFondoPicker={onOpenFondo}
        moodboard={moodboard}
        onOpenMoodboard={onOpenMoodboard}
      />

      {/* ── Instrucción ────────────────────────────────────────────────── */}
      {/* "Recientes" DENTRO del campo, abajo a la derecha: es el mismo botón —y el mismo
          historial— que ya tienen los otros paneles de instrucción
          (ADR-2026-08-12-historial-instrucciones-reusables), que v2 se había salteado al
          escribir su textarea de cero. El `pb-11` reserva su franja para que el texto no
          le pase por debajo, y el botón se esconde solo cuando no hay nada guardado. */}
      <div className="relative">
        <textarea
          value={otraConfig}
          onChange={(e) => onOtraChange(e.target.value)}
          placeholder="Agregá indicaciones extras para afinar el resultado"
          // 3 filas y no las 4 de antes: el `pb-11` que reserva la franja del botón suma
          // el alto de una línea, así que la card mide lo MISMO que medía y el campo no
          // le come espacio a la grilla de arriba.
          rows={3}
          className="w-full rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[14.5px] text-brand-black/90 placeholder:text-brand-black/35 resize-none transition-colors"
        />
        <PromptRecientesButton
          tipo="instruccion"
          onPick={onOtraChange}
          titulo="Instrucciones usadas antes"
          className="absolute bottom-2.5 right-2.5"
        />
      </div>

      </div>

      {/* ── Pie fijo: ajustes + Generar ─────────────────────────────────
          Vive en `PieConfiguracionModo` desde que lo comparte con Fotos de producto
          (ADR-2026-09-06-el-pie-de-configuracion-es-un-componente). Acá nació
          (ADR-2026-08-31-modelo-v2-ajustes-y-generar-fijos-abajo) y se ve igual: lo que
          cambió es dónde está escrito. */}
      <PieConfiguracionModo
        calidad={calidad}
        onCalidadChange={onCalidadChange}
        formatoLabel={formatoLabel}
        onOpenFormato={onOpenFormato}
        // La cantidad se elige en este modo; la de Fotos de producto es una cuenta
        // (producto × vista) y ahí el pie va sin contador.
        contador={
          contadorVisible
            ? { valor: cantidad, max: cantidadMax, onChange: onCantidadChange }
            : undefined
        }
        imagenesDeLaTanda={cantidadImagenes}
        creditCost={creditCost}
        onGenerate={onGenerate}
        falta={falta}
      />
    </div>
  );
};
